import React, { useEffect, useState } from "react";
import { IoIosArrowDown, IoIosArrowForward } from "react-icons/io";
import { useTranslation } from "react-i18next";
import {
  checkForWalletMin,
  hasRoles,
  removeItemWithExpiry,
  showToast,
} from "../../../lib/utility";
import CloseButton from "../../Common/CloseButton";
import Modal from "../../Common/Modal";
import { updateUserBalance, userSelector } from "../UserSlice";
import { useDispatch, useSelector } from "react-redux";
import useUserModuleAbac from "../../../hooks/useUserModuleAbac";
import { authSelector } from "../../Auth/AuthSlice";
import { middlewareUpdate } from "../../../redux/store";
import danger from "../../../assets/img/danger.svg";

const sectionHeaderStyle = {
  background: "#0000000a",
  border: "1px solid #3341631A",
  padding: "20px 24px",
  cursor: "pointer",
};

const sectionBodyStyle = {
  borderRadius: "0 0 20px 20px",
};

const legacyHeaderStyle = {
  background: "#0000000a",
  borderRadius: "20px 20px 0 0",
  border: "1px solid #3341631A",
  padding: "20px 24px",
};

const legacyBodyStyle = {
  borderRadius: "0 0 20px 20px",
};

const BalanceUser = ({
  modal,
  closeModal,
  userData,
  id,
  addBalance = false,
  userDataGetOnce,
}) => {
  const isTargetWallet = checkForWalletMin(userData?.walletFlag);
  const defaultSection = isTargetWallet ? "wallet" : "credit";

  const { t } = useTranslation("user");
  const [expandedSection, setExpandedSection] = useState(defaultSection);
  const [creditOption, setCreditOption] = useState("add");
  const [walletOption, setWalletOption] = useState("add");
  const [balanceOption, setBalanceOption] = useState("add");
  const [creditAmount, setCreditAmount] = useState(0);
  const [walletAmount, setWalletAmount] = useState(0);
  const [balanceData, setBalanceData] = useState({
    amount: 0,
    remarks: "",
    type: "Local",
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);

  const dispatch = useDispatch();
  const { isFetchingUserBalance } = useSelector(userSelector);
  const { user } = useSelector(authSelector);
  const { canUpdateBalance } = useUserModuleAbac();
  const showWalletCollapsible = hasRoles("superadmin,system,tenant", user);
  const canSelectTypeEnhanced = hasRoles("superadmin,system,tenant", user);
  const canSelectTypeLegacy = hasRoles("superadmin,system", user);

  // When an enhanced-role user (tenant/superadmin/system) tops up a low-tier
  // target (role exactly "user" or "admin"), restrict the modal to a single
  // balance type chosen by the target's walletFlag. Higher-tier targets keep
  // both credit + wallet sections.
  const isEnhancedBalanceRole = hasRoles("tenant,superadmin,system", user);
  const targetRole = (userData?.role || "").toString().toLowerCase().trim();
  const isTargetLowTier = targetRole === "user" || targetRole === "admin";
  const restrictToWalletFlag = isEnhancedBalanceRole && isTargetLowTier;
  const forcedSection = isTargetWallet ? "wallet" : "credit";

  const activeOption = showWalletCollapsible
    ? expandedSection === "wallet"
      ? walletOption
      : creditOption
    : balanceOption;
  const isWalletSubmit = showWalletCollapsible
    ? expandedSection === "wallet"
    : isTargetWallet;
  const activeAmount = showWalletCollapsible
    ? isWalletSubmit
      ? walletAmount
      : creditAmount
    : balanceData.amount;

  useEffect(() => {
    if (!modal) return;
    const wallet = checkForWalletMin(userData?.walletFlag);
    setExpandedSection(wallet ? "wallet" : "credit");
    setCreditOption("add");
    setWalletOption("add");
    setBalanceOption("add");
    setCreditAmount(0);
    setWalletAmount(0);
    setBalanceData({ amount: 0, remarks: "", type: "Local" });
    setShowConfirmModal(false);
    setIsConfirmed(false);
  }, [modal, userData?.id, userData?.walletFlag]);

  const toggleSection = (section) => {
    setExpandedSection(section);
  };

  const handleReset = () => {
    if (showWalletCollapsible) {
      setExpandedSection(defaultSection);
      setCreditOption("add");
      setWalletOption("add");
    } else {
      setBalanceOption("add");
    }
    setCreditAmount(0);
    setWalletAmount(0);
    setBalanceData({ amount: 0, remarks: "", type: "Local" });
  };

  const handleBalanceSubmit = () => {
    setShowConfirmModal(true);
  };

  const executeBalanceUpdate = () => {
    if (!canUpdateBalance) {
      showToast(
        "You do not have permission to update user balance.",
        "warning"
      );
      setShowConfirmModal(false);
      return;
    }
    const amount = Number(activeAmount) || 0;
    const creditPayloadAmount = Number(creditAmount) || 0;
    const walletPayloadAmount = Number(walletAmount) || 0;
    const operation = activeOption === "add" ? "plus" : "minus";

    const balanceBody = {
      userId: userData?.id || id,
      type: balanceData.type,
      toBeAddedWallet: showWalletCollapsible
        ? walletPayloadAmount
        : isWalletSubmit
          ? amount
          : 0,
      toBeAddedCredits: showWalletCollapsible
        ? creditPayloadAmount
        : isWalletSubmit
          ? 0
          : amount,
      remark: balanceData.remarks,
      walletFlag: isWalletSubmit ? "Yes" : "No",
      operation,
    };

    const username = userData?.username || userData?.systemId || "User";

    dispatch(updateUserBalance(balanceBody)).then((action) => {
      const isSuccess =
        action.payload &&
        !action.error &&
        action.payload?.status?.toString().startsWith("2");

      middlewareUpdate(
        action,
        `User Balance for ${username}`,
        () => {
          setTimeout(
            () => window.dispatchEvent(new CustomEvent("refreshWallet")),
            500
          );
        },
        undefined,
        {
          modal: closeModal,
          refresh: () => {
            removeItemWithExpiry("usersListtxnmode");
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent("forceUserListUpdate"));
            }, 500);
          },
          noToast: isSuccess,
        }
      );

      if (isSuccess) {
        showToast(
          `${t("balanceUser.successfullyUpdated")} ${username}`,
          "success"
        );
      }
    });
  };
  const renderAmountField = (mode) => (
    <div className="row mb-3">
      <div className="col-3">
        <label className="form-label mt-2 fs-16 fw-600 text-dark">
          {t("balanceUser.amountLabel")}:
        </label>
      </div>
      <div className="col-9">
        <input
          type="number"
          min={0}
          className="rounded form-control"
          placeholder={t("balanceUser.enterAmount")}
          value={
            showWalletCollapsible
              ? mode === "wallet"
                ? walletAmount
                : creditAmount
              : balanceData.amount
          }
          onChange={(e) => {
            const raw = e.target.value;
            if (showWalletCollapsible) {
              if (mode === "wallet") setWalletAmount(raw);
              else setCreditAmount(raw);
              return;
            }
            setBalanceData((prev) => ({
              ...prev,
              amount: raw,
            }));
          }}
        />
      </div>
    </div>
  );

  const renderUserBalanceRow = (isWalletMode) => (
    <div className="row mb-3">
      <div className="col-6">
        <label className="form-label mt-2 fs-16 fw-600 text-dark d-inline-block me-2">
          {t("balanceUser.userLabel")}:
        </label>
        <span className="fs-16 mt-2 d-inline-block">
          {userData?.username || userData?.systemId || "User"}
        </span>
      </div>
      <div className="col-6">
        <label className="form-label mt-2 fs-16 fw-600 text-dark d-inline-block me-2">
          {t("balanceUser.currentBalance")}:
        </label>
        <span className="fs-16 mt-2 d-inline-block">
          {Number(isWalletMode ? userData?.wallet ?? 0 : userData?.credit ?? 0).toFixed(2)}
        </span>
      </div>
    </div>
  );

  const renderCollapsibleSectionFields = (mode) => {
    const isWalletMode = mode === "wallet";
    const option = isWalletMode ? walletOption : creditOption;
    const setOption = isWalletMode ? setWalletOption : setCreditOption;

    return (
      <>
        <select
          className="form-select bg-transparent fw-600 mb-3"
          onChange={(e) => setOption(e.target.value)}
          value={option}
          disabled={addBalance && isWalletMode}
        >
          <option value="add">
            {t(isWalletMode ? "balanceUser.add" : "balanceUser.add")}{" "}
            {t(isWalletMode ? "balanceUser.wallet" : "balanceUser.credit")}{" "}
            {t("balanceUser.amount")}
          </option>
          <option value="deduct">
            {t(isWalletMode ? "balanceUser.deduct" : "balanceUser.deduct")}{" "}
            {t(isWalletMode ? "balanceUser.wallet" : "balanceUser.credit")}{" "}
            {t("balanceUser.amount")}
          </option>
        </select>
        {renderUserBalanceRow(isWalletMode)}
        {renderAmountField(isWalletMode ? "wallet" : "credit")}
      </>
    );
  };

  const renderTypeField = (showType) =>
    showType ? (
      <div className="row mb-3">
        <div className="col-3">
          <label className="form-label mt-2 fs-16 fw-600 text-dark">
            {t("balanceUser.typeLabel")}:
          </label>
        </div>
        <div className="col-9">
          <select
            className="rounded form-select"
            value={balanceData.type}
            onChange={(e) =>
              setBalanceData((prev) => ({
                ...prev,
                type: e.target.value,
              }))
            }
          >
            <option value="Local">{t("balanceUser.local")}</option>
            <option value="International">{t("balanceUser.international")}</option>
          </select>
        </div>
      </div>
    ) : null;

  const renderRemarksField = () => (
    <div className="row">
      <div className="col-3">
        <label className="form-label mt-2 fs-16 fw-600 text-dark">
          {t("balanceUser.remarksLabel")}:
        </label>
      </div>
      <div className="col-9">
        <input
          type="text"
          className="rounded form-control"
          placeholder={t("balanceUser.enterRemarks")}
          value={balanceData.remarks}
          onChange={(e) =>
            setBalanceData((prev) => ({
              ...prev,
              remarks: e.target.value,
            }))
          }
        />
      </div>
    </div>
  );

  const renderLegacyBody = () => (
    <>
      <div className="header" style={legacyHeaderStyle}>
        <select
          className="form-select bg-transparent fw-600"
          onChange={(e) => setBalanceOption(e.target.value)}
          value={balanceOption}
          disabled={addBalance}
        >
          <option value="add">
            {t(balanceOption ? (isTargetWallet ? "balanceUser.add" : "balanceUser.add") : "balanceUser.add")}{" "}
            {t(isTargetWallet ? "balanceUser.wallet" : "balanceUser.credit")}{" "}
            {t("balanceUser.amount")}
          </option>
          <option value="deduct">
            {t("balanceUser.deduct")}{" "}
            {t(isTargetWallet ? "balanceUser.wallet" : "balanceUser.credit")}{" "}
            {t("balanceUser.amount")}
          </option>
        </select>
      </div>
      <div key={balanceOption} className="p-4 border border-light_grey" style={legacyBodyStyle}>
        {renderUserBalanceRow(isTargetWallet)}
        {renderAmountField()}
        {renderTypeField(canSelectTypeLegacy)}
        {renderRemarksField()}
      </div>
    </>
  );

  const renderEnhancedBody = () => (
    <>
      {(!restrictToWalletFlag || forcedSection === "credit") && (
        <div className="mb-3">
          <div
            className="d-flex justify-content-between align-items-center fw-600"
            style={{
              ...sectionHeaderStyle,
              borderRadius: expandedSection === "credit" ? "20px 20px 0 0" : "20px",
            }}
            onClick={() => toggleSection("credit")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") toggleSection("credit");
            }}
          >
            <span>{t("balanceUser.add")} {t("balanceUser.credit")} {t("balanceUser.amount")}</span>
            {expandedSection === "credit" ? <IoIosArrowDown /> : <IoIosArrowForward />}
          </div>
          {expandedSection === "credit" && (
            <div className="p-4 border border-light_grey" style={sectionBodyStyle}>
              {renderCollapsibleSectionFields("credit")}
            </div>
          )}
        </div>
      )}
      {(!restrictToWalletFlag || forcedSection === "wallet") && (
        <div className="mb-3">
          <div
            className="d-flex justify-content-between align-items-center fw-600"
            style={{
              ...sectionHeaderStyle,
              borderRadius: expandedSection === "wallet" ? "20px 20px 0 0" : "20px",
            }}
            onClick={() => toggleSection("wallet")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") toggleSection("wallet");
            }}
          >
            <span>{t("balanceUser.add")} {t("balanceUser.wallet")} {t("balanceUser.amount")}</span>
            {expandedSection === "wallet" ? <IoIosArrowDown /> : <IoIosArrowForward />}
          </div>
          {expandedSection === "wallet" && (
            <div className="p-4 border border-light_grey" style={sectionBodyStyle}>
              {renderCollapsibleSectionFields("wallet")}
            </div>
          )}
        </div>
      )}
      {renderTypeField(canSelectTypeEnhanced)}
      {renderRemarksField()}
    </>
  );

  return (
    <>
      <Modal show={modal} close={closeModal} justify="center">
        <div className="modal-header">
          <h6 className="modal-title d-flex gap-2 align-items-center fs-22">
            {t("balanceUser.title")}
          </h6>
          <CloseButton theme="dark" onClick={closeModal} />
        </div>
        <div className="modal-body bg-white">
          {showWalletCollapsible ? renderEnhancedBody() : renderLegacyBody()}
        </div>
        <div className="modal-footer d-block bg-white">
          <div className="row g-3">
            <div className="col-6 mt-0">
              <button
                type="button"
                className="btn fs-16 fw-500 br-12 w-100 px-4 py-2 border_lightgray_1 me-2"
                onClick={handleReset}
              >
                {t("balanceUser.reset")}
              </button>
            </div>
            <div className="col-6 mt-0">
              <button
                type="submit"
                className="cbtn text-white btn w-100 fs-16 fw-500 br-12 px-4 py-2 border_lightgray_1"
                onClick={handleBalanceSubmit}
                disabled={isFetchingUserBalance}
              >
                {t("balanceUser.apply")}
              </button>
            </div>
          </div>
        </div>
      </Modal>
      {showConfirmModal && (
        <Modal
          show={showConfirmModal}
          close={() => {
            setShowConfirmModal(false);
            setIsConfirmed(false);
          }}
          justify="center"
        >
          <div className="modal-header">
            <h6 className="modal-title fs-22">{t("balanceUser.confirmTitle")}</h6>
            <CloseButton
              theme="dark"
              onClick={() => {
                setShowConfirmModal(false);
                setIsConfirmed(false);
              }}
            />
          </div>
          <div className="modal-body py-2 px-3 bg-white">
            <div className="d-flex align-items-center my-3">
              <input
                className="form-check-input float-none me-2"
                type="checkbox"
                id="confirmCheck"
                checked={isConfirmed}
                onChange={(e) => setIsConfirmed(e.target.checked)}
              />
              <label
                className="form-check-label fs-18 fw-600 text-dark"
                htmlFor="confirmCheck"
              >
                {t("balanceUser.confirmCheckbox")}
                <span className="text-danger">*</span>
              </label>
            </div>
            <div className="d-flex align-items-center py-3 px-3 shadow br-8">
              <img src={danger} alt="danger" loading="lazy" />
              <div className="ps-3">
                <h6 className="text-primary fs-16 fw-600 mb-1">
                  {t("balanceUser.confirmMessage").replace("{action}", activeOption === "add" ? t("balanceUser.add") : t("balanceUser.deduct"))}
                </h6>
                <p className="fs-16 fw-400 text-dark m-0">
                  {activeOption === "add"
                    ? t("balanceUser.adding")
                    : t("balanceUser.deducting")}{" "}
                  {activeAmount}{" "}
                  {isWalletSubmit
                    ? t("balanceUser.wallet")
                    : t("balanceUser.credit")}{" "}
                  {activeOption === "add"
                    ? t("balanceUser.to")
                    : t("balanceUser.from")}{" "}
                  <strong>
                    {userData?.username || userData?.systemId || "User"}
                  </strong>
                </p>
              </div>
            </div>
          </div>
          <div className="modal-footer d-block bg-white">
            <div className="row g-3">
              <div className="col-6 mt-0">
                <button
                  className="btn fs-16 fw-500 br-12 w-100 px-4 py-2 border_lightgray_1 me-2"
                  onClick={() => {
                    executeBalanceUpdate();
                    setShowConfirmModal(false);
                    setIsConfirmed(false);
                    closeModal();
                  }}
                  disabled={!isConfirmed}
                >
                  {t("balanceUser.confirm")}
                </button>
              </div>
              <div className="col-6 mt-0">
                <button
                  className="bg-primary-red text-white btn w-100 fs-16 fw-500 br-12 px-4 py-2 border_lightgray_1"
                  onClick={() => {
                    setShowConfirmModal(false);
                    setIsConfirmed(false);
                  }}
                >
                  {t("balanceUser.cancel")}
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default BalanceUser;
